import { label, percentage } from "./labels.ts";
import { validate } from "./validation.ts";
import type { Context, RecordValue, Schema, Value } from "./types.ts";

/** Keys belong to the editor, never to the request payload or an editable business identifier. */
export type EditorRow = { key: string; value: Value };
export function reconcileRows(previous: EditorRow[], values: Value[], createKey: () => string): EditorRow[] {
  const available = new Set(previous.map(row => row.key));
  const matched = values.map(value => {
    const match = previous.find(row => available.has(row.key) && Object.is(row.value, value));
    if (match) available.delete(match.key);
    return match;
  });
  return values.map((value, index) => {
    if (matched[index]) return { key: matched[index].key, value };
    const samePosition = values.length === previous.length && available.has(previous[index]?.key) ? previous[index] : undefined;
    if (samePosition) available.delete(samePosition.key);
    return { key: samePosition?.key || createKey(), value };
  });
}
export function replaceRow(rows: EditorRow[], key: string, value: Value): EditorRow[] {
  return rows.map(row => row.key === key ? { key, value } : row);
}
export function moveRow(rows: EditorRow[], key: string, direction: -1 | 1): EditorRow[] {
  const index = rows.findIndex(row => row.key === key);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= rows.length) return rows;
  const moved = [...rows];
  [moved[index], moved[target]] = [moved[target], moved[index]];
  return moved;
}
export function removeRow(rows: EditorRow[], key: string): EditorRow[] {
  return rows.filter(row => row.key !== key);
}
export function rowValidationIssues(schema: Schema, value: Value): string[] {
  const issues = validate(schema, value, true);
  if (value && typeof value === "object" && !Array.isArray(value) && value.type === "NUMBER" &&
    typeof value.min === "number" && typeof value.max === "number" && value.max <= value.min) issues.push("max");
  return [...new Set(issues)];
}
export function weightSummary(rows: Value[]): { total: number; invalidCount: number; complete: boolean } {
  let total = 0;
  let invalidCount = 0;
  for (const row of rows) {
    const weight = row && typeof row === "object" && !Array.isArray(row) ? row.weightBps : undefined;
    if (typeof weight === "number" && Number.isFinite(weight)) total += weight;
    if (typeof weight !== "number" || !Number.isSafeInteger(weight) || weight < 1 || weight > 10000) invalidCount++;
  }
  return { total, invalidCount, complete: rows.length > 0 && invalidCount === 0 && total === 10000 };
}
/** API/schema validation paths use one-based row positions; broad array errors affect every row. */
export function affectedRows(path: string, length: number, issues: string[]): number[] {
  const indices = new Set<number>();
  for (const issue of issues) {
    if (issue !== path && !issue.startsWith(path + ".")) continue;
    const segment = issue.slice(path.length + 1).split(".")[0];
    if (/^[1-9]\d*$/.test(segment)) {
      const index = Number(segment) - 1;
      if (index < length) indices.add(index);
    } else for (let index = 0; index < length; index++) indices.add(index);
  }
  return [...indices].sort((a, b) => a - b);
}
export function rowSummary(name: string, value: Value, index: number, context: Context, lang: string): { title: string; details: string[] } {
  const ar = lang === "ar";
  const record = value && typeof value === "object" && !Array.isArray(value) ? value : undefined;
  const names: Record<string, [string, string]> = {
    criteria: ["معيار", "Criterion"], sources: ["جولة مصدر", "Source round"], roleRules: ["دور", "Role"],
    options: ["خيار", "Option"], members: ["عضو", "Member"], entryIds: ["مشاركة", "Entry"], orderedUnitIds: ["موسم", "Season"],
  };
  const fallback = `${names[name]?.[ar ? 0 : 1] || (ar ? "عنصر" : "Item")} ${(index + 1).toLocaleString(lang)}`;
  const find = (rows: RecordValue[], field: string, id: Value | undefined, title: string) => rows.find(row => row[field] === id)?.[title];
  const unitName = (id: Value | undefined) => context.units.find(unit => unit.unitId === id)?.title || (context.unit && context.unit.unitId === id ? context.unit.title : undefined);
  if (!record) {
    const known = unitName(value) || find(context.entries, "entryId", value, "displayName") || find(context.people, "id", value, "name");
    return { title: known ? String(known) : fallback, details: [] };
  }
  const title = record.label || record.displayName || record.name || unitName(record.roundId || record.unitId) ||
    find(context.roleDefinitions, "roleCode", record.roleCode, "label") ||
    (record.roleCode === "PUBLIC" ? label("PUBLIC", lang) : undefined) ||
    find(context.people, "id", record.individualId, "name") || fallback;
  const details: string[] = [];
  if (name === "sources" && record.channel) {
    details.push(label(String(record.channel), lang));
    if (record.roleCode) details.push(String(find(context.roleDefinitions, "roleCode", record.roleCode, "label") || label(String(record.roleCode), lang)));
  }
  if (record.type) details.push(label(String(record.type), lang));
  if (typeof record.weightBps === "number" && Number.isFinite(record.weightBps)) details.push(`${ar ? "الوزن" : "Weight"} ${percentage(record.weightBps, lang)}`);
  if (typeof record.weightPoints === "number" && Number.isFinite(record.weightPoints)) details.push(`${ar ? "نقاط" : "Points"} ${record.weightPoints.toLocaleString(lang)}`);
  if (typeof record.scoreBps === "number" && Number.isFinite(record.scoreBps)) details.push(`${ar ? "الدرجة" : "Score"} ${percentage(record.scoreBps, lang)}`);
  if (name === "roleRules" && record.relationshipSource) details.push(label(String(record.relationshipSource), lang));
  if (record.inputMethod) details.push(label(String(record.inputMethod), lang));
  if (typeof record.weight === "number" && Number.isFinite(record.weight)) details.push(`${ar ? "وزن نسبي" : "Relative weight"} ${record.weight.toLocaleString(lang)}`);
  if (record.type === "NUMBER" && typeof record.min === "number" && typeof record.max === "number") details.push(`${record.min.toLocaleString(lang)}–${record.max.toLocaleString(lang)}`);
  if (record.type === "CHOICE" && Array.isArray(record.options)) details.push(`${record.options.length.toLocaleString(lang)} ${ar ? "خيارات" : "options"}`);
  return { title: String(title), details };
}
